class ComparePlansTable extends HTMLElement {
  constructor() {
    super();
    this.tooltipTriggers = this.querySelectorAll('[data-tooltip-trigger]');
    this.activeModal = null;
    this.activeModalClose = null;
    this.modalOriginalParent = null;
  }

  connectedCallback() {
    this.tooltipTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openModal(trigger);
      });
    });

    this.syncStickyColumnOffset();
    window.addEventListener('resize', () => this.syncStickyColumnOffset());
  }

  syncStickyColumnOffset() {
    //Flex-cols first content div
    const pricingRow = this.querySelector('thead th .flex-col.items-center > div:last-child');

    //Sticky cols pricing row. First div.
    const firstColTitleDiv = this.querySelector('.sticky.md\\:hidden > div:first-child');

    //Entire Sticky Column
    const stickyCol = this.querySelector('.sticky.md\\:hidden');

    if (!pricingRow || !firstColTitleDiv || !stickyCol) return;

    const headerCell = this.querySelector('th');

    //Convert the 2px border to a number, defaulting to 0 if not found.
    const theadBorder = parseFloat(getComputedStyle(headerCell).borderBottomWidth) || 0;

    //Calculate the bottom position of the pricing row and the first column title, then adjust the sticky column's padding accordingly.
    const pricingRowBottom = pricingRow.getBoundingClientRect().bottom;
    const firstColTitleBottom = firstColTitleDiv.getBoundingClientRect().bottom;
    const currentPadding = parseFloat(getComputedStyle(stickyCol).paddingTop) || 0;

    // The adjustment is the difference between the pricing row's bottom and the first column title's bottom, plus the thead border to account for any spacing.
    const adjustment = pricingRowBottom + theadBorder - firstColTitleBottom;
    stickyCol.style.paddingTop = `${currentPadding + adjustment}px`;
  }

  openModal(trigger) {
    // Close any existing modal first
    if (this.activeModal) {
      this.closeModal();
    }

    // Find the modal - it should be a sibling of the trigger in the same parent container
    let modal = trigger.parentElement?.querySelector('[data-tooltip-modal]');

    // If not found, try finding it in the parent's parent (the td or feature container)
    if (!modal) {
      const parentContainer = trigger.closest('td') || trigger.closest('[data-feature]') || trigger.closest('tr');
      modal = parentContainer?.querySelector('[data-tooltip-modal]');
    }

    // Last resort: search within the entire component
    if (!modal) {
      modal = this.querySelector('[data-tooltip-modal]');
    }

    if (!modal) {
      return;
    }

    this.activeModal = modal;
    this.activeModalClose = modal.querySelector('[data-tooltip-modal-close]');
    const modalContent = modal.querySelector('[data-tooltip-modal-content]');

    // Store original parent and move modal to body to escape stacking contexts
    this.modalOriginalParent = modal.parentNode;
    document.body.appendChild(modal);

    // Remove hidden and ensure flex is present
    modal.classList.remove('hidden');
    modal.classList.add('flex');
    document.body.classList.add('overflow-hidden');

    // Trigger slide-up animation on mobile
    requestAnimationFrame(() => {
      if (modalContent) {
        modalContent.classList.remove('translate-y-full');
        modalContent.classList.add('translate-y-0');
      }
      modal.classList.remove('opacity-0');
      modal.classList.add('opacity-100');
    });

    // Add event listeners
    if (this.activeModalClose) {
      this.activeModalClose.addEventListener('click', this.closeModal);
    }
    modal.addEventListener('click', this.handleOutsideClick);

    // Close on Escape key
    document.addEventListener('keydown', this.handleEscapeKey);
  }

  closeModal = () => {
    if (!this.activeModal) return;

    const modalContent = this.activeModal.querySelector('[data-tooltip-modal-content]');

    // Trigger slide-down animation on mobile
    if (modalContent) {
      modalContent.classList.remove('translate-y-0');
      modalContent.classList.add('translate-y-full');
    }
    this.activeModal.classList.remove('opacity-100');
    this.activeModal.classList.add('opacity-0');

    // Wait for animation to complete before hiding
    setTimeout(() => {
      this.activeModal.classList.add('hidden');
      this.activeModal.classList.remove('flex');
      document.body.classList.remove('overflow-hidden');

      // Move modal back to original parent
      if (this.modalOriginalParent && this.activeModal.parentNode === document.body) {
        this.modalOriginalParent.appendChild(this.activeModal);
      }

      // Remove event listeners
      if (this.activeModalClose) {
        this.activeModalClose.removeEventListener('click', this.closeModal);
      }
      this.activeModal.removeEventListener('click', this.handleOutsideClick);
      document.removeEventListener('keydown', this.handleEscapeKey);

      this.activeModal = null;
      this.activeModalClose = null;
      this.modalOriginalParent = null;
    }, 300); // Match transition duration
  };

  handleOutsideClick = (e) => {
    if (e.target === this.activeModal) {
      this.closeModal();
    }
  };

  handleEscapeKey = (e) => {
    if (e.key === 'Escape' && this.activeModal) {
      this.closeModal();
    }
  };
}

if (!customElements.get('compare-plans-table')) {
  customElements.define('compare-plans-table', ComparePlansTable);
}
